import React, { useEffect, useState, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

function ProcessMassBalanceChart({ range = '7d', start, end }) {
  const [values, setValues] = useState({
    discard: 0,
    ultraFines: 0,
    product: 0,
  });
  const [feed, setFeed] = useState(0);
  const chartRef = useRef(null);
  const [legendHeight, setLegendHeight] = useState(0);

  useEffect(() => {
    const params = start && end
      ? `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
      : `time_range=${range}`;
    fetch(`/api/process-mass-balance.php?${params}`)
      .then(res => res.json())
      .then(res => {
        setValues({
          discard: res.discard ?? 0,
          ultraFines: res.ultra_fines ?? 0,
          product: res.product ?? 0,
        });
        setFeed(parseFloat(res.total_feed) || 0);
      })
      .catch(() => {
        setValues({ discard: 0, ultraFines: 0, product: 0 });
        setFeed(0);
      });
  }, [range, start, end]);

  useEffect(() => {
    if (chartRef.current) {
      setLegendHeight(chartRef.current.legend?.height || 0);
    }
  }, []);

  const data = {
    labels: [
      'Coarse & Fines Discard %',
      'Ultra Fines %',
      'Coarse & Fines Product %',
    ],
    datasets: [
      {
        data: [values.discard, values.ultraFines, values.product],
        backgroundColor: ['#22c55e', '#ffffff', '#3b82f6'],
        borderColor: ['#22c55e', '#ffffff', '#3b82f6'],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { animateRotate: true, duration: 800 },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#ffffff',
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.label}: ${ctx.parsed.toFixed(1)}%`,
        },
      },
    },
  };

  const labelPlugin = {
    id: 'doughnutLabel',
    afterDatasetsDraw: chart => {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const ds = chart.data.datasets[0];
      meta.data.forEach((arc, i) => {
        const value = ds.data[i];
        if (value === 0) return;
        const angle = (arc.startAngle + arc.endAngle) / 2;
        const radius = (arc.outerRadius + arc.innerRadius) / 2;
        const x = arc.x + radius * Math.cos(angle);
        const y = arc.y + radius * Math.sin(angle);
        ctx.save();
        ctx.fillStyle = '#000';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${value.toFixed(1)}%`, x, y);
        ctx.restore();
      });
    },
  };

  return (
    <div className="relative flex-1 mt-2">
      <Doughnut ref={chartRef} data={data} options={options} plugins={[labelPlugin]} />
      <div
        className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none text-white"
        style={{ top: legendHeight, bottom: 0 }}
      >
        <span className="font-semibold text-sm">Feed to plant</span>
        <span className="text-xs">{feed.toLocaleString()} t</span>
      </div>
    </div>
  );
}

export default ProcessMassBalanceChart;
